import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { unregister as unregisterServiceWorker } from './registerServiceWorker';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import './i18n';

// MSAL imports
import { MsalProvider } from "@azure/msal-react";
import { msalInstance } from './services/auth.service';
import { setInitialAuthError } from './authErrorStore';

// Polyfill global for legacy libraries
if (typeof (window as any).global === 'undefined') {
  (window as any).global = window;
}
if (typeof (window as any).process === 'undefined') {
  (window as any).process = { env: {} };
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

const baseUrl = document.getElementsByTagName('base')[0]?.getAttribute('href') || '/';

// Vite does not generate the legacy CRA service worker. Remove any installation
// left by an older deployment so it cannot serve stale public assets.
unregisterServiceWorker();

// Initialize MSAL and render
(async () => {
  try {
    await msalInstance.initialize();

    // Handle redirect promise to ensure state is clean before mounting.
    // A rejection here (e.g. AADSTS error returned in the hash) must not be
    // swallowed silently: otherwise App mounts and re-fires login → redirect loop.
    // Surface it so the error screen renders instead of retrying.
    try {
      await msalInstance.handleRedirectPromise();
    } catch (redirectError) {
      console.error('MSAL redirect handling failed:', redirectError);
      setInitialAuthError(redirectError);
    }

    const root = createRoot(rootElement);

    root.render(
      <React.StrictMode>
        <BrowserRouter
          basename={baseUrl}
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <MsalProvider instance={msalInstance}>
            <QueryClientProvider client={queryClient}>
              <App />
            </QueryClientProvider>
          </MsalProvider>
        </BrowserRouter>
      </React.StrictMode>
    );
  } catch (error) {
    console.error("Failed to initialize app:", error);
  }
})();
